// src/admin/pages/PlanListPage.jsx — superadmin «Piani» (route /admin/plans), VIEW-ONLY since v223.
// Plans are changed by the technical team through the backend CLI, never from here: no create, no edit.
//
// Two independent reads (Promise.allSettled): GET /admin/plans (the table) and GET /admin/plan-features (each plan's
// family + the families × features grid). Either may fail alone: without the grid the table is one flat list by
// price; without the plans the grid still shows.
// ⛔ One sequence ticket guards both reads (a stale «Riprova» answer never lands over a fresh one).
// ⛔ Never err.message: failures are worded by adminErrorText (code / status / request reference).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminGetPlanFeatures, adminListPlans } from '../adminApi.js';
import { adminErrorText } from '../adminErrorCopy.js';
import { groupPlans } from '../plans/plansModel.js';
import PlansTable from '../plans/PlansTable.jsx';
import PlanFeaturesGrid from '../plans/PlanFeaturesGrid.jsx';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const note = (tone) => ({
  marginBottom: '16px', padding: '12px 16px', borderRadius: RADII.controlInner,
  background: tone.bg, color: tone.fg, fontSize: '13px', display: 'flex', gap: '12px', alignItems: 'center',
});
const retryStyle = {
  border: 'none', background: 'transparent', color: COLORS.accent, fontWeight: 600, cursor: 'pointer',
  fontFamily: FONT_SANS, fontSize: '13px', padding: 0, textDecoration: 'underline',
};

export default function PlanListPage() {
  const { t } = useTranslation('admin');
  const [state, setState] = useState({ loading: true, plans: null, plansError: null, grid: null, gridError: null });
  const ticket = useRef(0);

  const load = useCallback(async () => {
    ticket.current += 1;
    const mine = ticket.current;
    setState((s) => ({ ...s, loading: true, plansError: null, gridError: null }));
    const [p, g] = await Promise.allSettled([adminListPlans(), adminGetPlanFeatures()]);
    if (mine !== ticket.current) return;
    setState({
      loading: false,
      plans: p.status === 'fulfilled' ? p.value : null,
      plansError: p.status === 'rejected' ? p.reason : null,
      grid: g.status === 'fulfilled' ? g.value : null,
      gridError: g.status === 'rejected' ? g.reason : null,
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => groupPlans(state.plans, state.grid), [state.plans, state.grid]);
  const familyOrder = useMemo(() => groups.map((g) => g.family), [groups]);
  const retry = <button type="button" onClick={load} style={retryStyle}>{t('plans.retry')}</button>;

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1280px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{t('plans.title')}</h1>
        <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: 0 }}>{t('plans.subtitle')}</p>
      </div>

      {state.plansError && (
        <div role="alert" style={note(SEMANTIC.errore)}>
          <span>{t('plans.loadError', { reason: adminErrorText(t, state.plansError) })}</span>{retry}
        </div>
      )}
      {state.gridError && !state.plansError && (
        <div role="status" style={note(SEMANTIC.attenzione)}>
          <span>{t('plans.gridError', { reason: adminErrorText(t, state.gridError) })}</span>{retry}
        </div>
      )}

      <div style={{ background: COLORS.surface, borderRadius: RADII.control, border: `1px solid ${COLORS.border}`, padding: '24px', overflowX: 'auto' }}>
        {state.loading && !state.plans
          ? <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: 0 }}>{t('shared.loading')}</p>
          : (state.plans && <PlansTable groups={groups} />)}
        {state.grid && <PlanFeaturesGrid grid={state.grid} familyOrder={familyOrder} />}
      </div>
    </div>
  );
}
